import { useContext, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — header navigation dropdown with click-outside.
 *
 * YAML usage:
 *   - type: NavDropdown
 *     label: ~.label
 *     children: ~.children        # array of strings OR array of {label, url}
 *     isFirst: ~.isFirst          # optional, adds the accent underline
 *     triggerClass: "..."         # optional override for button/anchor classes
 *     menuClass: "..."            # optional override for the dropdown panel
 *     itemClass: "..."            # optional override for each menu link
 *     accentClass: "..."          # optional override for the isFirst underline
 *
 * Class overrides fully replace the defaults — pass the complete list. The
 * defaults below match the democrats.eu theme; Corsair blocks pass their own.
 */
const DEFAULT_TRIGGER = 'flex items-center gap-0.5 text-sm text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap'
const DEFAULT_ACCENT  = 'font-medium border-b-2 border-[#E85D26] pb-0.5'
const DEFAULT_MENU    = 'absolute left-0 top-full mt-2 w-52 bg-white border border-gray-100 rounded shadow-lg z-50 py-1'
const DEFAULT_ITEM    = 'block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors'

export const NavDropdown = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const label = resolve(props.label)
  const children = resolve(props.children)
  const isFirst = resolve(props.isFirst)
  const triggerClass = resolve(props.triggerClass) ?? DEFAULT_TRIGGER
  const accentClass  = resolve(props.accentClass)  ?? DEFAULT_ACCENT
  const menuClass    = resolve(props.menuClass)    ?? DEFAULT_MENU
  const itemClass    = resolve(props.itemClass)    ?? DEFAULT_ITEM

  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const items = Array.isArray(children) ? children : []
  const hasChildren = items.length > 0

  if (!hasChildren) {
    return (
      <ActionDependant {...props}>
        <a
          href="#"
          className={triggerClass}
          onClick={(e) => e.preventDefault()}
        >
          {label}
        </a>
      </ActionDependant>
    )
  }

  return (
    <ActionDependant {...props}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={`${triggerClass} ${isFirst ? accentClass : ''}`}
        >
          {label}
          <ChevronDown
            size={13}
            className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div className={menuClass}>
            {items.map((child, idx) => {
              const childLabel = typeof child === 'string' ? child : child?.label
              const childUrl = typeof child === 'string' ? '#' : (child?.url ?? '#')
              return (
                <a
                  key={`${childLabel}-${idx}`}
                  href={childUrl}
                  className={itemClass}
                  onClick={(e) => {
                    if (childUrl === '#') e.preventDefault()
                    setOpen(false)
                  }}
                >
                  {childLabel}
                </a>
              )
            })}
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
